import {
    XFCStatus,
} from 'lib/xfConnect';
import {events} from 'lib';
import TC from 'services/testConnect2p0';
import {xfName, SCAN_ERROR_KEY, xfcScanStatuses} from 'helpers/constants';
import {getErrorCode, ScanningErrors} from './errorCodes';

// How many ms to wait between calls to check the scanner progress.
const CHECK_INTERVAL = 1000;

// How many ms to prepare to dip the strip in water
const PREP_TIME = 3000;

// How many ms to dip the strip in water
const DIP_TIME = 2000;

// How many ms to insert the shuttle
const INSERT_TIME = 18000;

// How many ms to analyze the water
const ANALYZE_TIME = 60000;

class XFCProgressTracker {
  _interval = undefined;
  _checkProgressPromise = undefined;
  _lastProgressText = '';
  _startTime = undefined;
  _currentStatus = undefined;

  /**
  * Begins the progress tracker of the XFC scanning process
  * @returns {void}
  */
  start = () => {
    if (this._interval !== undefined) { return; }

    this.reset();
    this._interval = setInterval(this.checkProgress, CHECK_INTERVAL);
  }

  /**
  * Stops the progress tracker of the XFC scanning process
  * @returns {void}
  */
  stop = () => {
    if (this._interval === undefined) { return; }

    clearInterval(this._interval);
    this.reset();
    this._interval = undefined;
  }

  /**
  * Resets the progress tracker's timer and sets a new xfc scan status if provided
  * @param {string} [newStatus=undefined] Current XF scan status
  * @returns {void}
  */
  reset = (newStatus = undefined) => {
    this._startTime = new Date().getTime();
    this._currentStatus = newStatus;
  }

  /**
   * Processes the errors from the XFC scan and publishes an event to cancel the scan
   *
   * @param {Object<string>} err Error object
   * @returns {void}
   */
  _processScanningError(err) {
    console.error(err);
    if (err.code !== undefined) {

      let message = err.message ? err.message : getErrorCode(err.code, xfName);

    // Publish event to display error banner.
      events.publish('error.user', {
        message: message,
        key: SCAN_ERROR_KEY,
      });
    }

    this.reset();
    TC.reset();
    this.stop();
    events.publish('scanner.cancel');
  }

  /**
   * Main function to track the XFC scanning progress by sending a 'status' request.
   * New progress is published to the event API if progress has been made.
   * Final scan results from 'status' request are published to a scan complete event.
   * @returns {void}
   */
  checkProgress = () => {
    if (this._checkProgressPromise !== undefined) { return; }

    this._checkProgressPromise = XFCStatus()
        .then((response) => {

          // Stop checking progress if device has been disconnected
          if (!response.deviceConnected) {
            this._processScanningError({code: ScanningErrors.XFC_SERVICEOFFLINE});
            return;
          }

          let progress;
          let progressText;
          let currTime;
          let timeElapsed;

          // Case when scanning is complete and results are ready
          if (response.systemMode === xfcScanStatuses.RESULTS) {
            this.reset();
            TC.removeCurrentScanner();
            events.publish('scanner.complete', response.results);
            this.stop();
            return;

          // Case when the scanner is waiting for the user to insert the shuttle
          } else if (response.systemMode.includes(xfcScanStatuses.INSERT_SHUTTLE)) {
            currTime = new Date().getTime();

            // Includes used since another state called 'InsertingShuttle_NotInYet' (same thing)
            if (!response.systemMode.includes(this._currentStatus)) {
              this.reset(response.systemMode);
            }
            timeElapsed = currTime - this._startTime;

            progress = timeElapsed / (PREP_TIME + DIP_TIME + INSERT_TIME) * 100;

            if (timeElapsed < PREP_TIME + DIP_TIME) {
              progressText = 'scanning.progress-bar.xfc-dip';
            } else {
              progressText = 'scanning.progress-bar.xfc-insert';
            }

          // Case when scanner is analyzing the strips - no user action needed here
          } else if (response.systemMode === xfcScanStatuses.ANALYZING) {
            events.publish('scanner.progress-text', '');
            currTime = new Date().getTime();

            if (this._currentStatus !== response.systemMode) {
              this.reset(response.systemMode);
            }

            timeElapsed = currTime - this._startTime;
            progress = timeElapsed / ANALYZE_TIME * 100;

          } else { // Case when the system hangs for 15 seconds or more on an unknown status
            timeElapsed = currTime - this._startTime;
            if (timeElapsed >= 15000) {
              console.error(response);
              this._processScanningError({code: ScanningErrors.XFC_SERVICEOFFLINE});
              return;
            }
          }

          if (this._interval === undefined) {
            this.stop();
            return;
          }

          events.publish('scanner.progress', progress);

          if (progressText && progressText !== '') {
            events.publish('scanner.progress-text', progressText);
          }
        })
            .catch((err) => {
              this._processScanningError(err);
            })
            .finally(() => {
              this._checkProgressPromise = undefined;
            });
  }
}

let xfcProgressTracker = new XFCProgressTracker();

export default xfcProgressTracker;